<script lang="ts" setup>
import { ref, watch, defineEmits, defineProps } from 'vue'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import {BatchStateValue} from "@/types/batch-types";

const props = defineProps<{
  title: string
  dropdownLabel: string
  dropdownOptions: { label: string; value: string }[]
  modelValue: string | null
  disabled?: boolean
  placeholder?: string
  showCurrencyFilter?: boolean
  currency?: number
  currencyOptions?: { label: string; value: number }[]
  singleDateMode?: boolean
  showBatchStateFilter?: boolean
  batchState?: BatchStateValue | string | null
  batchStateOptions?: { label: string; value: BatchStateValue | string }[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'update:currency', value: number): void
  (e: 'update:batchState', value: BatchStateValue | null | string): void
  (e: 'update:startDate', value: Date | null): void
  (e: 'update:endDate', value: Date | null): void
  (e: 'generate-report'): void
}>()

const localStartDate = ref<Date | null>(null)
const localEndDate = ref<Date | null>(null)
const localSelected = ref<string | null>(props.modelValue ?? 'All')
const localCurrency = ref<number>(props.currency ?? 0)
const localBatchState = ref<BatchStateValue | string | null>(props.batchState ?? 'All')

watch(() => props.modelValue, (val) => {
  localSelected.value = val ?? 'All'
})

watch(() => props.currency, (val) => {
  localCurrency.value = val ?? 0
})

watch(() => props.batchState, (val) => {
  localBatchState.value = val ?? 'All'
})

watch(localSelected, (val) => {
  emit('update:modelValue', val)
})

watch(localCurrency, (val) => {
  emit('update:currency', val)
})

watch(localBatchState, (val) => {
  emit('update:batchState', val)
})

watch(localStartDate, (val) => emit('update:startDate', val))
watch(localEndDate, (val) => emit('update:endDate', val))

const handleGenerateReportClick = () => {
  emit('generate-report')
}
</script>

<template>
  <PageHeader
      :title="title"
      buttonText="Create Report"
      buttonIcon="fluent-mdl2:report-add"
      @click="handleGenerateReportClick"
  />

  <div class="bg-white p-6 rounded-xl space-y-6 mt-4">
    <div v-if="singleDateMode" class="flex justify-center">
      <div class="w-full max-w-md">
        <label for="singleDate" class="block text-sm font-medium text-neutral-700 mb-1 text-center">Report Date:</label>
        <div class="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <Calendar
              inputId="singleDate"
              v-model="localStartDate"
              dateFormat="dd/mm/yy"
              showIcon
              class="w-full [&_input]:border-none [&_input]:focus:ring-0"
          />
        </div>
      </div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label for="startDate" class="block text-sm font-medium text-neutral-700 mb-1">Start Date:</label>
        <div class="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <Calendar
              inputId="startDate"
              v-model="localStartDate"
              dateFormat="dd/mm/yy"
              showIcon
              class="w-full [&_input]:border-none [&_input]:focus:ring-0"
          />
        </div>
      </div>

      <div>
        <label for="endDate" class="block text-sm font-medium text-neutral-700 mb-1">End Date:</label>
        <div class="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <Calendar
              inputId="endDate"
              v-model="localEndDate"
              dateFormat="dd/mm/yy"
              showIcon
              class="w-full [&_input]:border-none [&_input]:focus:ring-0"
          />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div v-if="!singleDateMode || dropdownOptions.length > 1">
        <label :for="dropdownLabel.toLowerCase().replace(' ', '')" class="block text-sm font-medium text-neutral-700 mb-1">
          {{ dropdownLabel }}:
        </label>
        <Dropdown
            :inputId="dropdownLabel.toLowerCase().replace(' ', '')"
            v-model="localSelected"
            :options="dropdownOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="placeholder || `Select a ${dropdownLabel}`"
            :disabled="disabled"
            class="w-full"
            :showClear="localSelected !== 'All'"
        />
      </div>

      <div v-if="showCurrencyFilter">
        <label for="currency" class="block text-sm font-medium text-neutral-700 mb-1">Currency:</label>
        <Dropdown
            inputId="currency"
            v-model="localCurrency"
            :options="currencyOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Currency"
            class="w-full"
        />
      </div>

      <div v-if="showBatchStateFilter">
        <label for="batchState" class="block text-sm font-medium text-neutral-700 mb-1">Batch State:</label>
        <Dropdown
            inputId="batchState"
            v-model="localBatchState"
            :options="batchStateOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Batch State"
            class="w-full"
            :showClear="true"
        />
      </div>
    </div>
  </div>
</template>